import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DashboardFolderT } from 'business/definitions';
import { RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';

const renameFolder = async (name: string, id: number) => {
    const response = await RQAPICallWithData<DashboardFolderT>({
        endpoint: `explore/dashboard_folders/${id}`,
        body: { name },
        method: 'PUT',
    });
    return response.content;
};

/**
 * RQ Mutation to rename a dashboard folder.
 * The mutation takes as a parameter a name, which is the new name to apply to the dashboard
 * folder, and an id that corresponds to the id of the dashboard folder to rename.
 */
export const useRenameFolder = () => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: ({ name, id }: { name: string; id: number }) =>
            renameFolder(name, id),

        onSuccess: ({ data: renamedDashboardFolder, message }) => {
            dispatch.vision.notifyUser({
                message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                ['dashboard_folders'],
                (oldDashboardFolders: DashboardFolderT[]) =>
                    oldDashboardFolders.map((folder) =>
                        folder.id === renamedDashboardFolder.id
                            ? renamedDashboardFolder
                            : folder,
                    ),
            );
        },
        meta: { useGlobalOnError: true },
    });
};
